import { SSOAccountSwitchModal } from '@/components/auth/SSOAccountSwitchModal';
import { router, useGlobalSearchParams } from 'expo-router';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Platform } from 'react-native';
import { useAuth } from './AuthContext';
import { ValidationError } from './errors';
import { logger } from './logger';
import { supabase } from './supabase';
import {
  fetchWordPressSsoPayload,
  handleSSOTokenExchange,
  verifySsoMagicLinkToken,
} from './wordpress-redirect';

type SSOTokenContextValue = {
  ssoProcessing: boolean;
  ssoToken: string | undefined;
  ssoError: Error | null;
  retry: () => void;
  shouldRedirectToLogin: boolean;
};

const SSOTokenContext = createContext<SSOTokenContextValue | null>(null);

function normEmail(e: string | undefined | null) {
  return (e ?? '').trim().toLowerCase();
}

export function SSOTokenProvider({ children }: { children: ReactNode }) {
  const { user, loading, signOut } = useAuth();
  const searchParams = useGlobalSearchParams();
  const ssoToken = searchParams.sso_token as string | undefined;

  const [ssoProcessing, setSsoProcessing] = useState(false);
  const [ssoError, setSsoError] = useState<Error | null>(null);
  const [switchOpen, setSwitchOpen] = useState(false);
  const [switchTargetEmail, setSwitchTargetEmail] = useState('');
  const [switchBusy, setSwitchBusy] = useState(false);

  const processedTokenRef = useRef<string | null>(null);
  const pendingSwitchTokenRef = useRef<string | null>(null);
  const pendingSwitchMagicHashRef = useRef<string | null>(null);

  useEffect(() => {
    if (Platform.OS !== 'web' || !ssoToken || loading) return;
    if (processedTokenRef.current === ssoToken) return;

    processedTokenRef.current = ssoToken;

    const token = ssoToken;

    const cleanUrl = () => {
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        const url = new URL(window.location.href);
        url.searchParams.delete('sso_token');
        router.replace((url.pathname + url.search) as any);
      }
    };

    (async () => {
      setSsoError(null);

      try {
        // Anonymous users coming back from WP signup look like an existing user
        // (anon row in auth.users) but should be treated as "not logged in" for
        // the SSO exchange so the real Supabase session cleanly replaces the
        // anon one — no switch-account modal, no empty-email mismatch.
        const treatAsAnonOrMissing = !user || !!user.is_anonymous;

        if (treatAsAnonOrMissing) {
          // WP SSO tokens are one-time: the peek below consumes it, so a
          // later handleSSOTokenExchange(token) would always get 401. Keep
          // the magic-link hash the peek returned and verify that instead.
          let peekedAccessToken: string | null = null;
          const { data: existingSessionData } = await supabase.auth.getSession();
          if (existingSessionData?.session && !existingSessionData.session.user?.is_anonymous) {
            const peek = await fetchWordPressSsoPayload(token);
            const sessionEmail = normEmail(existingSessionData.session.user?.email);
            const tokenEmail = peek.ok ? normEmail(peek.email) : sessionEmail;
            if (!tokenEmail || tokenEmail === sessionEmail) {
              cleanUrl();
              return;
            }
            if (peek.ok) peekedAccessToken = peek.accessToken;
          }

          setSsoProcessing(true);
          const result = peekedAccessToken
            ? await verifySsoMagicLinkToken(peekedAccessToken)
            : await handleSSOTokenExchange(token);
          if (!result.success) {
            const { data: fallback } = await supabase.auth.getSession();
            if (!fallback.session || fallback.session.user?.is_anonymous) {
              setSsoError(result.error ?? new Error('SSO token exchange failed'));
            }
          }
          cleanUrl();
          return;
        }

        const payload = await fetchWordPressSsoPayload(token);
        if (!payload.ok) {
          cleanUrl();
          return;
        }

        const target = normEmail(payload.email);
        const current = normEmail(user.email);

        if (!target || target === current) {
          cleanUrl();
          return;
        }

        pendingSwitchTokenRef.current = token;
        pendingSwitchMagicHashRef.current = payload.accessToken;
        setSwitchTargetEmail(payload.email.trim());
        setSwitchOpen(true);
      } catch (error) {
        logger.error('SSO token exchange error:', error);
        setSsoError(error instanceof Error ? error : new Error('An unexpected error occurred'));
        cleanUrl();
      } finally {
        setSsoProcessing(false);
      }
    })();
  }, [ssoToken, loading, user?.id]);

  const onStaySignedIn = useCallback(() => {
    setSwitchOpen(false);
    setSwitchTargetEmail('');
    pendingSwitchTokenRef.current = null;
    pendingSwitchMagicHashRef.current = null;
    processedTokenRef.current = null;
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      url.searchParams.delete('sso_token');
      router.replace((url.pathname + url.search) as any);
    }
  }, []);

  const onConfirmSwitch = useCallback(async () => {
    const magicHash = pendingSwitchMagicHashRef.current;
    if (!magicHash) {
      logger.error('SSO switch: missing cached access token');
      setSwitchOpen(false);
      return;
    }

    const returnPath =
      Platform.OS === 'web' && typeof window !== 'undefined'
        ? window.location.pathname
        : undefined;

    setSwitchBusy(true);
    setSsoProcessing(true);
    try {
      await signOut();
      setSsoError(null);
      const result = await verifySsoMagicLinkToken(magicHash);
      if (result.success && returnPath) {
        router.replace(returnPath as any);
      }
      if (!result.success) {
        setSsoError(result.error ?? new Error('SSO token exchange failed'));
      }
    } catch (error) {
      logger.error('SSO switch verify error:', error);
      setSsoError(error instanceof Error ? error : new Error('An unexpected error occurred'));
    } finally {
      setSsoProcessing(false);
      setSwitchBusy(false);
      setSwitchOpen(false);
      setSwitchTargetEmail('');
      pendingSwitchTokenRef.current = null;
      pendingSwitchMagicHashRef.current = null;
      processedTokenRef.current = null;
    }
  }, [signOut]);

  const retry = useCallback(() => {
    processedTokenRef.current = null;
    setSsoError(null);
  }, []);

  const shouldRedirectToLogin = ssoError instanceof ValidationError;

  const value = useMemo(
    () => ({
      ssoProcessing,
      ssoToken,
      ssoError,
      retry,
      shouldRedirectToLogin,
    }),
    [ssoProcessing, ssoToken, ssoError, retry, shouldRedirectToLogin],
  );

  const currentEmail = user?.email ?? '';

  return (
    <SSOTokenContext.Provider value={value}>
      {children}
      {Platform.OS === 'web' && (
        <SSOAccountSwitchModal
          open={switchOpen}
          currentEmail={currentEmail}
          targetEmail={switchTargetEmail}
          busy={switchBusy}
          onStay={onStaySignedIn}
          onConfirm={onConfirmSwitch}
        />
      )}
    </SSOTokenContext.Provider>
  );
}

export function useSSOTokenExchange(): SSOTokenContextValue {
  const ctx = useContext(SSOTokenContext);
  if (!ctx) {
    throw new Error('useSSOTokenExchange must be used within SSOTokenProvider');
  }
  return ctx;
}
